import { action, computed, observable } from 'mobx'
import type { FetchMemberCouponsFn } from '@/features/billing'
import type {
  GenerateMemberMagicLinkFn,
  GetCurrentMemberFn,
  ImpersonateMemberFn,
  ListUserTeamMembersForWorkspaceFn,
} from '@/features/iam'
import type { Membership } from '@/features/iam/workspaces/Membership'
import type { FetchJobTotalStatusAggregationsFn } from '@/features/jobs'
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  map,
  merge,
  Subject,
  switchMap,
} from 'rxjs'
import { type Coupon } from '@/features/billing/coupons/Coupon'
import { type UserCoupon } from '@/features/billing/coupons/UserCoupon'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { type TeamMember } from '@/features/iam/teams/TeamMember'
import { toStream } from '@/utils/mobx-util'
import { filterNonNil, withAbortSignal } from '@/utils/rx-util'
import { type AddUserToTeamDialogViewModel } from '@/features/user-management/AddUserToTeamDialog/AddUserToTeamDialogViewModel'
import { type EditUserRoleDialogViewModel } from '@/features/user-management/EditUserRoleDialog/EditUserRoleDialogViewModel'
import { type RemoveFromWorkspaceDialogViewModel } from '@/features/user-management/RemoveFromWorkspaceDialog/RemoveFromWorkspaceDialogViewModel'
import { type RestoreToWorkspaceDialogViewModel } from '@/features/user-management/RestoreToWorkspaceDialog/RestoreToWorkspaceDialogViewModel'
import { type ManageUserCapacityDialogViewModel } from '@/features/user-management/ManageUserCapacityDialog/ManageUserCapacityDialogViewModel'
import { type ManageSkillsListDialogViewModel } from '@/features/user-management/ManageSkillsListDialog/ManageSkillsListDialogViewModel'
import { type PromoteToOwnerDialogViewModel } from '@/features/user-management/PromoteToOwnerDialog/PromoteToOwnerDialogViewModel'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { isNotNil } from '@/utils/object-util'
import { sum } from 'lodash-es'
import { type MembershipJobTotals } from '@/features/user-management/components/MembershipsSidebar'
import { type JobTotalStatusAggregations } from '@/features/jobs/dashboard/job-stats/job-totals'
import { TabController } from '@/infra/TabController'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { MembershipItemViewModel } from './MembershipItemViewModel'

export type MembershipTab = 'Active' | 'Inactive'

/**
 * The dialogs that the Memberships tab opens.
 */
export interface MembershipsTabDialogs {
  addUserToTeamVm: AddUserToTeamDialogViewModel
  addUserToWorkspaceVm: { modal: { show: () => void } }
  addCustomerToWorkspaceVm: { modal: { show: () => void } }
  joinWorkspaceAsAdminVm: { modal: { show: () => void } }
  editUserRoleVm: EditUserRoleDialogViewModel
  removeFromWorkspaceVm: RemoveFromWorkspaceDialogViewModel
  restoreToWorkspaceVm: RestoreToWorkspaceDialogViewModel
  manageUserCapacityVm: ManageUserCapacityDialogViewModel
  manageSkillListVm: ManageSkillsListDialogViewModel
  promoteToOwnerVm: PromoteToOwnerDialogViewModel
}

/**
 * View Model for the Memberships tab.
 */
export class MembershipsTabViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _refreshSelectedMembership$ = new Subject<void>()
  private readonly _membershipVms = new ViewModelList({
    models: () => this.memberships,
    create: (membership: Membership) =>
      this._createMembershipItemViewModel(membership),
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  /**
   * Search text to filter memberships by.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Tab controller for the Membership Tabs.
   */
  readonly tabController = new TabController<MembershipTab>(() => [
    {
      tab: 'Active',
      title: 'Active',
      hasError: () => false,
    },
    {
      tab: 'Inactive',
      title: 'Inactive',
      hasError: () => false,
    },
  ])

  /**
   * The currently selected workspace membership.
   */
  @observable
  accessor selectedMembership: Membership | null = null

  /**
   * Number of active jobs for the currently selected workspace membership.
   */
  @observable
  accessor jobTotals: Partial<MembershipJobTotals> = {
    totalJobs: 0,
    activeJobs: 0,
  }

  /**
   * Coupons for the currently selected workspace membership.
   */
  @observable
  accessor coupons: UserCoupon[] = []

  /**
   * List of team members for the currently selected workspace membership.
   */
  @observable
  accessor teamMembers: TeamMember[] = []

  private readonly _membershipDetails$ = merge(
    toStream(() => this.selectedMembership, false).pipe(distinctUntilChanged()),
    this._refreshSelectedMembership$.pipe(
      map(() => this.selectedMembership),
      filterNonNil(),
    ),
  ).pipe(
    switchMap((membership) => {
      if (membership === null) {
        this.reset()
        return EMPTY
      }

      return withAbortSignal((signal) =>
        this._fetchMembershipDetails(membership, signal),
      ).pipe(
        // The retry alert has reported the failure.
        catchError(() => EMPTY),
      )
    }),
  )

  constructor(
    private readonly _user: () => SearchableUser,
    private readonly _dialogs: MembershipsTabDialogs,
    private readonly _canJoinWorkspaceAsAdmin: () => boolean,
    private readonly _canPromoteToOwner: () => boolean,
    private readonly _listUserTeamMembersForWorkspace: ListUserTeamMembersForWorkspaceFn,
    private readonly _impersonateMember: ImpersonateMemberFn,
    private readonly _generateMemberMagicLink: GenerateMemberMagicLinkFn,
    private readonly _fetchMemberCoupons: FetchMemberCouponsFn,
    private readonly _fetchJobTotalStatusAggregations: FetchJobTotalStatusAggregationsFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._lifecycle.manage(this._membershipVms)
    this._lifecycle.manage(this._membershipDetails$)
  }

  /**
   * Activates the view model.
   */
  activate() {
    this._lifecycle.activate()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this._lifecycle.deactivate()
    this.reset()
  }

  /**
   * The currently selected user.
   */
  @computed
  get user() {
    return this._user()
  }

  /**
   * Whether the currently selected user is a provider.
   */
  @computed
  get isPro() {
    return this.user.userDataType === 'Pro'
  }

  /**
   * The currently selected user's workspace memberships.
   */
  @computed
  get memberships() {
    return this.user.memberships
  }

  /**
   * The workspace memberships count based on the currently selected tab.
   */
  @computed
  get membershipsCount() {
    switch (this.tabController.current.tab) {
      case 'Active':
        return this.activeMemberships.length

      case 'Inactive':
        return this.inactiveMemberships.length
    }
  }

  /**
   * Whether the search text is empty.
   */
  @computed
  get isSearchTextEmpty() {
    return this.searchText.value === ''
  }

  @computed
  get activeMemberships() {
    const activeMemberships = this.memberships.filter(
      (membership) => !membership.member.inactive,
    )

    if (this.isSearchTextEmpty) {
      return activeMemberships
    }

    const searchText = this.searchText.value.toLowerCase()

    return activeMemberships.filter((membership) =>
      membership.workspace.name.toLowerCase().includes(searchText),
    )
  }

  @computed
  get inactiveMemberships() {
    const inactiveMemberships = this.memberships.filter(
      (membership) => membership.member.inactive,
    )

    if (this.isSearchTextEmpty) {
      return inactiveMemberships
    }

    const searchText = this.searchText.value.toLowerCase()

    return inactiveMemberships.filter((membership) =>
      membership.workspace.name.toLowerCase().includes(searchText),
    )
  }

  /**
   * Whether the memberships tab sidebar is open.
   */
  @computed
  get isSidebarOpen() {
    return this.selectedMembership !== null
  }

  /**
   * The redeemed coupons of the selected membership, as the sidebar lists them.
   */
  @computed
  get redeemedCoupons() {
    return this.coupons.map((userCoupon) => ({
      code: userCoupon.coupon.code,
      value: formatCouponValue(userCoupon.coupon),
      dateRedeemed: userCoupon.dateRedeemed,
    }))
  }

  /**
   * The skills of the selected membership, as the sidebar lists them.
   */
  @computed
  get selectedMembershipSkills() {
    return (this.selectedMembership?.member.skills ?? []).map((skill) => ({
      id: skill.id,
      name: skill.name,
      description: skill.description,
    }))
  }

  /**
   * The teams of the selected membership, as the sidebar lists them.
   */
  @computed
  get selectedMembershipTeams() {
    return this.teamMembers.map((teamMember) => ({
      id: teamMember.team.id,
      name: teamMember.team.name,
      roleName: teamMember.teamRole.name,
    }))
  }

  /**
   * The view model of the membership open in the sidebar.
   */
  @computed
  get selectedMembershipVm() {
    return this.selectedMembership
      ? (this._membershipVms.get(this.selectedMembership) ?? null)
      : null
  }

  /**
   * The memberships listed in the current tab.
   */
  @computed
  get currentMemberships() {
    switch (this.tabController.current.tab) {
      case 'Active':
        return this.activeMemberships
      case 'Inactive':
        return this.inactiveMemberships
    }
  }

  /**
   * The view models of the memberships listed in the current tab.
   */
  @computed
  get currentMembershipVms() {
    return this.currentMemberships
      .map((membership) => this._membershipVms.get(membership))
      .filter(isNotNil)
  }

  /**
   * Whether the current user can join workspaces as admin.
   */
  @computed
  get canJoinWorkspaceAsAdmin() {
    return this._canJoinWorkspaceAsAdmin()
  }

  /**
   * Whether the currently selected user is a workspace customer.
   */
  @computed
  get isCustomerUser() {
    return this._user().isWorkspaceCustomer
  }

  /**
   * What the add button does for the viewed user.
   */
  @computed
  get addAction(): MembershipAddAction {
    if (this.canJoinWorkspaceAsAdmin) {
      return 'JoinWorkspaceAsAdmin'
    }
    return this.isCustomerUser ? 'AddCustomerToWorkspace' : 'AddUserToWorkspace'
  }

  /**
   * Sets the current membership tab.
   */
  @action.bound
  setCurrentMembershipTab(tab: MembershipTab) {
    this.tabController.change(tab)
    this.clearFilters()
  }

  /**
   * Sets the currently selected workspace membership.
   */
  @action.bound
  setSelectedMembership(membership: Membership | null) {
    this.selectedMembership = membership
  }

  /**
   * Sets the number of active jobs for the currently selected workspace membership.
   */
  @action.bound
  setJobTotals(jobTotals: JobTotalStatusAggregations) {
    this.jobTotals = {
      totalJobs: sum(Object.values(jobTotals)),
      activeJobs: jobTotals.claimed + jobTotals.idle,
      claimedJobs: jobTotals.claimed,
      onHoldJobs: jobTotals.onHold,
      idleJobs: jobTotals.idle,
      closedJobs: jobTotals.closed,
      underConstructionJobs: jobTotals.underConstruction,
      unclaimedJobs: jobTotals.unclaimed,
      infoGatheringJobs: jobTotals.infoGathering,
    }
  }

  /**
   * Sets the coupons for the currently selected workspace membership.
   */
  @action.bound
  setCoupons(coupons: UserCoupon[]) {
    this.coupons = coupons
  }

  /**
   * Sets the team members for the currently selected workspace membership.
   */
  @action.bound
  setTeamMembers(teamMembers: TeamMember[]) {
    this.teamMembers = teamMembers
  }

  /**
   * Opens the memberships tab sidebar and sets the current workspace membership.
   */
  @action.bound
  openSidebar(membership: Membership) {
    if (this.selectedMembership === membership) {
      this.closeSidebar()
      return
    }

    this.setSelectedMembership(membership)
  }

  /**
   * Closes the memberships tab sidebar.
   */
  @action.bound
  closeSidebar() {
    this.setSelectedMembership(null)
  }

  /**
   * Reloads the sidebar details of the selected membership, if any.
   */
  refreshSelectedMembership() {
    this._refreshSelectedMembership$.next()
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
  }

  /**
   * Resets the view model.
   */
  @action.bound
  reset() {
    this.setSelectedMembership(null)
    this.setCoupons([])
    this.setTeamMembers([])
    this.jobTotals = { totalJobs: 0, activeJobs: 0 }
    this.clearFilters()
  }

  /**
   * Opens the dialog behind the add button.
   */
  @action.bound
  add() {
    switch (this.addAction) {
      case 'JoinWorkspaceAsAdmin':
        this._dialogs.joinWorkspaceAsAdminVm.modal.show()
        break
      case 'AddCustomerToWorkspace':
        this._dialogs.addCustomerToWorkspaceVm.modal.show()
        break
      case 'AddUserToWorkspace':
        this._dialogs.addUserToWorkspaceVm.modal.show()
        break
    }
  }

  /**
   * Fetches the number of active and total jobs for the given workspace membership.
   */
  private async _fetchJobTotalsForMembership(
    membership: Membership,
    signal: AbortSignal,
  ) {
    const userPublicId = membership.member.user.publicId
    const workspaceId = membership.workspace.id

    const projection = await this._fetchJobTotalStatusAggregations(
      { userPublicId, workspaceId },
      signal,
    )
    signal.throwIfAborted()

    this.setJobTotals(projection)
  }

  /**
   * Fetches the coupons for the given workspace membership.
   */
  private async _fetchCouponsForMembership(
    membership: Membership,
    signal: AbortSignal,
  ) {
    const redeemedCoupons = await this._fetchMemberCoupons(
      membership.member,
      signal,
    ).then((coupons) => coupons.filter((coupon) => coupon.redeemed))
    signal.throwIfAborted()

    this.setCoupons(redeemedCoupons)
  }

  /**
   * Fetches the team members for the given workspace membership.
   */
  private async _fetchTeamMembersForMembership(
    membership: Membership,
    signal: AbortSignal,
  ) {
    const userPublicId = membership.member.user.publicId
    const workspaceId = membership.workspace.id

    const teamMembers = await this._listUserTeamMembersForWorkspace(
      { userPublicId, workspaceId },
      signal,
    )
    signal.throwIfAborted()

    this.setTeamMembers(teamMembers)
  }

  /**
   * Fetches the sidebar details for the given workspace membership. A failed
   * detail leaves its section empty rather than failing the others.
   */
  private async _fetchMembershipDetails(
    membership: Membership,
    signal: AbortSignal,
  ) {
    const withRetry = this._createAlertRetryWrapper(
      'Loading membership details failed',
      signal,
    )
    await withRetry(() =>
      Promise.all([
        this._fetchJobTotalsForMembership(membership, signal),
        this.isPro
          ? undefined
          : this._fetchCouponsForMembership(membership, signal),
        this._fetchTeamMembersForMembership(membership, signal),
      ]),
    )
  }

  private _createMembershipItemViewModel(membership: Membership) {
    return new MembershipItemViewModel(
      membership,
      this._user,
      () => this.selectedMembership,
      this.openSidebar,
      this._dialogs,
      this._canPromoteToOwner,
      this._impersonateMember,
      this._generateMemberMagicLink,
      this._getCurrentMember,
      this._newAlert,
      this._createAlertErrorHandler,
    )
  }
}

function formatCouponValue(coupon: Coupon) {
  switch (coupon.offType.type) {
    case 'AmountOff':
      return `$${coupon.offType.amountOff} OFF`
    case 'PercentOff':
      return `${coupon.offType.percentOff}% OFF`
  }
}

export type MembershipAddAction =
  'JoinWorkspaceAsAdmin' | 'AddCustomerToWorkspace' | 'AddUserToWorkspace'

export interface AdminAccess {
  canImpersonateAsClient: boolean
  canImpersonateAsPro: boolean
  canAddUserToTeam: boolean
  canEditUserRole: boolean
  canEditUserSkills: boolean
  canManageUserCapacity: boolean
  canManageWorkspaceMemberships: boolean
}
